import React from 'react';
import { AlertCircle, ChevronDown, RefreshCw, X } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppConfirmDialog } from '../../../components/AppConfirmDialog';
import { AppToastViewport } from '../../../components/AppToastViewport';
import { useToasts } from '../../../hooks/useToasts';
import { PositionQuoteOffersPopover, type QuoteOffersPositionContext } from './PositionQuoteOffersPopover';
import type { ComparisonTitle, PositionQuoteOffer, PriceMatrix } from '../quoteComparison';

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const euro4 = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 4 });
const qtyFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

type MatrixResponse = PriceMatrix & { no_lv?: boolean; error?: string };

interface Props {
  ausschreibungId: string;
  hasLv: boolean;
  quoteCount: number;
  canEditProjects: boolean;
  /** Nach Übernahme: Akte (LV-Kennzahlen) neu laden. */
  onPricesApplied: () => void | Promise<void>;
  /** Nach Zuordnung: Angebote neu laden (matched counts). */
  onMatched: () => void | Promise<void>;
}

type Filter = 'all' | 'unpriced' | 'proposed';

/**
 * Preisvergleich Positionen × Lieferanten: günstigstes Angebot je Position, Abdeckung und Summen
 * je Lieferant, Übernahme einzeln (Zellklick), je Lieferant oder „günstigste je Position“.
 */
export const TenderPriceMatrix: React.FC<Props> = ({ ausschreibungId, hasLv, quoteCount, canEditProjects, onPricesApplied, onMatched }) => {
  const [matrix, setMatrix] = React.useState<MatrixResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [includeProposed, setIncludeProposed] = React.useState(false);
  const [filter, setFilter] = React.useState<Filter>('all');
  /** Angeklickter Lieferant: nur Positionen zeigen, zu denen er eine Zeile hat (andere Spalten bleiben). */
  const [supplierFilter, setSupplierFilter] = React.useState<string | null>(null);
  /** Eingeklappte Titel (IDs). Untertitel und Positionen darunter sind ausgeblendet. */
  const [collapsed, setCollapsed] = React.useState<Set<string>>(() => new Set());
  const [matching, setMatching] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [confirmApply, setConfirmApply] = React.useState<{ mode: 'cheapest' | 'quote'; quoteId?: string; label: string } | null>(null);
  const [openPositionId, setOpenPositionId] = React.useState<string | null>(null);
  /** Zelle, deren Preis gerade übernommen wird (positionId:quoteId). */
  const [applyingCell, setApplyingCell] = React.useState<string | null>(null);

  /** Klick auf einen Preis: diesen EK direkt übernehmen (auch Vorschläge, der Klick ist die Bestätigung). */
  const applyCell = async (positionId: string, quoteId: string) => {
    setApplyingCell(`${positionId}:${quoteId}`);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/apply-prices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'explicit',
          picks: [{ position_id: positionId, quote_id: quoteId }],
          position_ids: [positionId],
          include_proposed: true,
        }),
      });
      const result = (await res.json().catch(() => ({}))) as { error?: string; updated?: unknown[]; skipped?: Array<{ reason: string }> };
      if (!res.ok) throw new Error(result.error || 'Übernahme fehlgeschlagen.');
      if (!result.updated?.length) {
        pushToast(result.skipped?.[0]?.reason ? `Nicht übernommen: ${result.skipped[0].reason}` : 'Nicht übernommen.', 'error');
        return;
      }
      // Sofort in der Matrix zeigen; Summen und Akte ziehen im Hintergrund nach.
      setMatrix((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          positions: prev.positions.map((pos) => {
            if (pos.id !== positionId) return pos;
            const offer = pos.offers.find((o) => o.quoteId === quoteId);
            if (!offer || offer.unitEkPerLvUnit == null) return pos;
            return { ...pos, currentEk: offer.unitEkPerLvUnit, ekSourceQuoteItemId: offer.primaryItemId };
          }),
        };
      });
      void Promise.all([load(), onPricesApplied()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplyingCell(null);
    }
  };
  const { toasts, pushToast, dismissToast } = useToasts();

  /** „EK aktuell“ leeren: EK auf 0, Herkunft weg; sofort in der Matrix, Akte zieht nach. */
  const clearEk = async (positionId: string) => {
    setApplyingCell(`${positionId}:clear`);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/apply-prices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'revert', position_ids: [positionId] }),
      });
      const result = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(result.error || 'Leeren fehlgeschlagen.');
      setMatrix((prev) =>
        prev
          ? { ...prev, positions: prev.positions.map((pos) => (pos.id === positionId ? { ...pos, currentEk: 0, ekSourceQuoteItemId: null } : pos)) }
          : prev,
      );
      void Promise.all([load(), onPricesApplied()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplyingCell(null);
    }
  };
  const [open, setOpen] = React.useState(true);
  const [suppliersOpen, setSuppliersOpen] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!hasLv) {
      setMatrix(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/price-matrix${includeProposed ? '?include_proposed=1' : ''}`,
        { credentials: 'include' },
      );
      const body = (await res.json().catch(() => null)) as MatrixResponse | null;
      if (!res.ok || !body) throw new Error(body?.error || 'Preisvergleich konnte nicht geladen werden.');
      setMatrix(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ausschreibungId, hasLv, includeProposed]);

  React.useEffect(() => {
    void load();
  }, [load, quoteCount]);

  const runMatchAll = async () => {
    setMatching(true);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/match-all`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; results?: Array<{ matched?: number; total?: number; error?: string }> };
      if (!res.ok) throw new Error(body.error || 'Zuordnung fehlgeschlagen.');
      const matched = (body.results ?? []).reduce((a, r) => a + (r.matched ?? 0), 0);
      const total = (body.results ?? []).reduce((a, r) => a + (r.total ?? 0), 0);
      const failed = (body.results ?? []).filter((r) => r.error).length;
      pushToast(`${matched} von ${total} Angebotszeilen zugeordnet${failed ? `, ${failed} Angebot(e) mit Fehler` : ''}.`, failed ? 'info' : 'success');
      await Promise.all([load(), onMatched()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setMatching(false);
    }
  };

  const applyPrices = async (body: Record<string, unknown>) => {
    setApplying(true);
    setError(null);
    try {
      const res = await fetch(`/api/ausschreibungen/${encodeURIComponent(ausschreibungId)}/apply-prices`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_proposed: includeProposed, ...body }),
      });
      const result = (await res.json().catch(() => ({}))) as { error?: string; updated?: unknown[]; skipped?: Array<{ reason: string }> };
      if (!res.ok) throw new Error(result.error || 'Übernahme fehlgeschlagen.');
      pushToast(
        `${result.updated?.length ?? 0} Positionen übernommen${result.skipped?.length ? `, ${result.skipped.length} übersprungen` : ''}.`,
      );
      await Promise.all([load(), onPricesApplied()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setApplying(false);
    }
  };

  const rows = React.useMemo(() => {
    if (!matrix) return [];
    return matrix.positions.filter((p) => {
      if (supplierFilter && !p.offers.some((o) => o.quoteId === supplierFilter && o.unitEkPerLvUnit != null)) return false;
      if (filter === 'unpriced') return !(p.currentEk > 0);
      if (filter === 'proposed') return p.offers.some((o) => o.status === 'proposed');
      return true;
    });
  }, [matrix, filter, supplierFilter]);

  React.useEffect(() => {
    if (supplierFilter && matrix && !matrix.suppliers.some((s) => s.quoteId === supplierFilter)) setSupplierFilter(null);
  }, [matrix, supplierFilter]);

  type MatrixRow = { position: PriceMatrix['positions'][number] } | { title: ComparisonTitle; count: number; hidden: boolean };
  type PositionRow = Extract<MatrixRow, { position: unknown }>;
  type TitleRow = Extract<MatrixRow, { title: unknown }>;

  /** Positionen in LV-Reihenfolge, davor die Titelkette; eingeklappte Titel blenden alles darunter aus. */
  const displayRows = React.useMemo<MatrixRow[]>(() => {
    if (!matrix) return [];
    const titleById = new Map(matrix.titles.map((t) => [t.id, t]));
    const chainOf = (titleId: string | null | undefined): ComparisonTitle[] => {
      const chain: ComparisonTitle[] = [];
      let cur = titleId ? titleById.get(titleId) : undefined;
      while (cur) {
        chain.unshift(cur);
        cur = cur.parentId ? titleById.get(cur.parentId) : undefined;
      }
      return chain;
    };
    const out: MatrixRow[] = [];
    const titleRows = new Map<string, TitleRow>();
    for (const p of rows) {
      const chain = chainOf(p.titleId);
      for (let i = 0; i < chain.length; i += 1) {
        const t = chain[i];
        let tr = titleRows.get(t.id);
        if (!tr) {
          tr = { title: t, count: 0, hidden: chain.slice(0, i).some((a) => collapsed.has(a.id)) };
          titleRows.set(t.id, tr);
          out.push(tr);
        }
        tr.count += 1;
      }
      if (!chain.some((t) => collapsed.has(t.id))) out.push({ position: p });
    }
    return out;
  }, [matrix, rows, collapsed]);

  const toggleTitle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  /** Zwei Angebote desselben Lieferanten: Datum anhängen, damit die Spalten unterscheidbar sind. */
  const supplierLabel = React.useCallback(
    (s: PriceMatrix['suppliers'][number]) => {
      const dup = matrix ? matrix.suppliers.filter((x) => x.supplierName === s.supplierName).length > 1 : false;
      return dup && s.quoteDate ? `${s.supplierName} (${new Date(s.quoteDate).toLocaleDateString('de-DE')})` : s.supplierName;
    },
    [matrix],
  );
  const topLevelTitleIds = React.useMemo(
    () => (matrix ? matrix.titles.filter((t) => !t.parentId).map((t) => t.id) : []),
    [matrix],
  );
  const allCollapsed = topLevelTitleIds.length > 0 && topLevelTitleIds.every((id) => collapsed.has(id));

  const openPosition = React.useMemo(() => (matrix && openPositionId ? matrix.positions.find((p) => p.id === openPositionId) ?? null : null), [matrix, openPositionId]);
  const openContext = React.useMemo<QuoteOffersPositionContext | null>(
    () =>
      openPosition
        ? {
            positionId: openPosition.id,
            label: `${openPosition.oz ? `${openPosition.oz} ` : ''}${openPosition.name}`.trim(),
            quantity: openPosition.quantity,
            unit: openPosition.unit ?? '',
            currentEk: openPosition.currentEk,
            appliedQuoteItemId: openPosition.ekSourceQuoteItemId,
          }
        : null,
    [openPosition],
  );

  if (!hasLv) return null;

  return (
    <>
      <section className="rounded-lg border border-app-border bg-app-surface shadow-sm">
        <div className={`flex items-center justify-between gap-3 px-4 py-3 ${suppliersOpen ? 'border-b border-app-border' : ''}`}>
          <button
            type="button"
            onClick={() => setSuppliersOpen((o) => !o)}
            className="flex items-center gap-2 min-w-0 text-left"
            aria-expanded={suppliersOpen}
          >
            <ChevronDown className={`w-4 h-4 text-app-text-subtle shrink-0 transition-transform ${suppliersOpen ? '' : '-rotate-90'}`} />
            <h2 className="text-base font-semibold text-app-text truncate">Lieferantenübersicht</h2>
            {matrix && <span className="text-xs text-app-text-subtle">· {matrix.suppliers.length}</span>}
          </button>
          {supplierFilter && (
            <button
              type="button"
              onClick={() => setSupplierFilter(null)}
              className="rounded px-2.5 py-1 text-sm bg-blue-50 text-pipedrive-blue hover:bg-blue-100"
              title="Lieferantenfilter aufheben"
            >
              Filter aufheben
            </button>
          )}
        </div>
        <div className={suppliersOpen ? '' : 'hidden'}>
          {!matrix || matrix.suppliers.length === 0 ? (
            <p className="px-4 py-3 text-sm text-app-text-muted">Noch keine gelesenen Angebote mit Zuordnung.</p>
          ) : null}
        {matrix && matrix.suppliers.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-app-text-subtle">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Lieferant</th>
                  <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Positionen</th>
                  <th className="px-4 py-2 text-right font-medium whitespace-nowrap">Abdeckung</th>
                  <th className="px-4 py-2 text-right font-medium whitespace-nowrap">abgedeckt</th>
                  <th className="px-4 py-2 text-right font-medium whitespace-nowrap" title="Summe über Positionen, die alle Lieferanten abdecken">
                    vergleichbar
                  </th>
                  <th className="px-4 py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {matrix.suppliers.map((s) => {
                  const selected = supplierFilter === s.quoteId;
                  return (
                    <tr
                      key={s.quoteId}
                      onClick={() => setSupplierFilter((cur) => (cur === s.quoteId ? null : s.quoteId))}
                      className={`border-t border-app-border cursor-pointer transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-app-hover'}`}
                      title={selected ? 'Filter aufheben' : 'Nur Positionen mit Angebot dieses Lieferanten zeigen'}
                    >
                      <td className="px-4 py-2 font-medium text-app-text">{supplierLabel(s)}</td>
                      <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{s.coveredPositions}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-app-text-muted whitespace-nowrap">{s.coveragePct} %</td>
                      <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">{euro.format(s.totalCovered)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium whitespace-nowrap">{euro.format(s.totalComparable)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmApply({ mode: 'quote', quoteId: s.quoteId, label: `Alle Positionen von ${s.supplierName}` });
                          }}
                          disabled={applying || !canEditProjects || s.coveredPositions === 0}
                          className="text-pipedrive-blue hover:underline disabled:opacity-50 disabled:no-underline"
                        >
                          Alles übernehmen
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t border-app-border bg-app-surface-muted/40">
                  <td className="px-4 py-2 font-medium text-app-text">Summen</td>
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                    <span className="text-app-text-subtle">vergleichbar </span>{matrix.comparablePositionCount}
                  </td>
                  <td></td>
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                    <span className="text-app-text-subtle">aktuell EK </span>
                    <span className="font-medium text-app-text">{euro.format(matrix.currentTotal)}</span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums whitespace-nowrap">
                    <span className="text-app-text-subtle">günstigste </span>
                    <span className="font-medium text-emerald-700">{euro.format(matrix.cheapestTotal)}</span>
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        </div>
      </section>

    <section className="rounded-lg border border-app-border bg-app-surface shadow-sm">
      <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3 ${open ? 'border-b border-app-border' : ''}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 min-w-0 text-left"
          aria-expanded={open}
        >
          <ChevronDown className={`w-4 h-4 text-app-text-subtle shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
          <h2 className="text-base font-semibold text-app-text truncate">Preisvergleich</h2>
          {loading && <LoadingSpinner size="xs" />}
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-sm text-app-text-muted">
            <input type="checkbox" checked={includeProposed} onChange={(e) => setIncludeProposed(e.target.checked)} />
            Vorschläge mitzählen
          </label>
          <button
            type="button"
            onClick={() => void runMatchAll()}
            disabled={matching || quoteCount === 0}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text disabled:opacity-50 transition"
            title="Alle gelesenen Angebote den LV-Positionen zuordnen (OZ, Artikelnummer, dann KI)"
          >
            {matching && <LoadingSpinner size="xs" />}
            Zuordnung starten
          </button>
          <button
            type="button"
            onClick={() => setConfirmApply({ mode: 'cheapest', label: 'Günstigstes Angebot je Position' })}
            disabled={applying || !canEditProjects || !matrix || matrix.positions.every((p) => !p.cheapestQuoteId)}
            className="inline-flex items-center gap-1.5 rounded-md bg-pipedrive-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition"
            title={!canEditProjects ? 'Recht „Projekte bearbeiten“ nötig' : 'EK aus dem günstigsten zugeordneten Angebot je Position übernehmen'}
          >
            {applying && <LoadingSpinner size="xs" />}
            Günstigste übernehmen
          </button>
          <button type="button" onClick={() => void load()} className="p-1.5 rounded hover:bg-app-hover text-slate-400" title="Neu laden">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className={`px-4 py-3 space-y-3 ${open ? '' : 'hidden'}`}>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 inline-flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {matrix && matrix.suppliers.length === 0 ? (
          <p className="text-sm text-app-text-muted">Noch keine gelesenen Angebote mit Zuordnung. Erst Angebote hochladen, dann „Zuordnung starten“.</p>
        ) : matrix ? (
          <>
            <div className="flex items-center gap-1 text-sm">
              {(
                [
                  ['all', 'alle'],
                  ['unpriced', 'ohne EK'],
                  ['proposed', 'Vorschläge prüfen'],
                ] as Array<[Filter, string]>
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={`rounded px-2.5 py-1 ${filter === key ? 'bg-pipedrive-blue text-white' : 'bg-app-surface-muted text-app-text-muted hover:bg-app-hover'}`}
                >
                  {label}
                </button>
              ))}
              {supplierFilter && (
                <button
                  type="button"
                  onClick={() => setSupplierFilter(null)}
                  className="rounded px-2.5 py-1 bg-blue-50 text-pipedrive-blue hover:bg-blue-100"
                  title="Lieferantenfilter aufheben"
                >
                  nur {matrix.suppliers.find((s) => s.quoteId === supplierFilter)?.supplierName ?? 'Lieferant'} ×
                </button>
              )}
              {topLevelTitleIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(topLevelTitleIds))}
                  className="rounded px-2.5 py-1 text-app-text-muted hover:bg-app-hover"
                >
                  {allCollapsed ? 'Titel ausklappen' : 'Titel einklappen'}
                </button>
              )}
              <span className="ml-auto text-app-text-subtle">{rows.length} Positionen</span>
            </div>
            <div className="app-scrollbar overflow-auto max-h-[60vh] -mx-4 -mb-3 border-t border-app-border">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-app-surface text-app-text-subtle z-10">
                  <tr>
                    <th className="sticky left-0 bg-app-surface pl-10 pr-2 py-2 text-left font-medium">OZ</th>
                    <th className="px-2 py-1.5 text-left font-medium min-w-[14rem]">Position</th>
                    <th className="px-2 py-1.5 text-right font-medium whitespace-nowrap">Menge</th>
                    <th className="px-2 py-1.5 text-right font-medium whitespace-nowrap">EK aktuell</th>
                    {matrix.suppliers.map((s) => (
                      <th key={s.quoteId} className="px-2 py-1.5 text-right font-medium whitespace-nowrap max-w-[12rem] truncate" title={supplierLabel(s)}>
                        {supplierLabel(s)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((row) => {
                    if ('title' in row) {
                      if (row.hidden) return null;
                      const isCollapsed = collapsed.has(row.title.id);
                      return (
                        <tr
                          key={`t-${row.title.id}`}
                          className="border-t border-app-border bg-app-surface-muted/70 cursor-pointer hover:bg-app-surface-muted"
                          onClick={() => toggleTitle(row.title.id)}
                        >
                          <td className="sticky left-0 bg-app-surface-muted/70 pl-4 pr-2 py-2 whitespace-nowrap">
                            <span
                              className="inline-flex items-center gap-2 font-medium text-app-text tabular-nums"
                              style={{ paddingLeft: `${Math.max(0, row.title.level - 1) * 1}rem` }}
                            >
                              <ChevronDown className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                              {row.title.oz ?? ''}
                            </span>
                          </td>
                          <td colSpan={3 + matrix.suppliers.length} className="px-2 py-2 font-medium text-app-text">
                            {row.title.name}
                            <span className="ml-2 text-app-text-subtle font-normal">{row.count}</span>
                          </td>
                        </tr>
                      );
                    }
                    const p = (row as PositionRow).position;
                    const appliedQuoteId = p.ekSourceQuoteItemId
                      ? p.offers.find((o) => o.lines.some((l) => l.itemId === p.ekSourceQuoteItemId))?.quoteId ?? null
                      : null;
                    return (
                      <tr key={p.id} className={`border-t border-app-border ${p.isEventual ? 'text-app-text-muted' : ''}`}>
                        <td className="sticky left-0 bg-app-surface pl-10 pr-2 py-2 tabular-nums text-app-text-muted whitespace-nowrap">{p.oz ?? ''}</td>
                        <td className="px-2 py-2">
                          <button type="button" onClick={() => setOpenPositionId(p.id)} className="text-left hover:text-pipedrive-blue line-clamp-2" title="Angebote zur Position">
                            {p.name}
                          </button>
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
                          {qtyFmt.format(p.quantity)} {p.unit ?? ''}
                        </td>
                        <td className="group/ek px-2 py-2 text-right tabular-nums whitespace-nowrap text-app-text">
                          {applyingCell === `${p.id}:clear` ? (
                            <span className="inline-flex w-full justify-center"><LoadingSpinner size="xs" /></span>
                          ) : p.currentEk > 0 ? (
                            <span className="inline-flex items-center justify-end gap-1">
                              {euro4.format(p.currentEk)}
                              {canEditProjects && (
                                <button
                                  type="button"
                                  onClick={() => void clearEk(p.id)}
                                  disabled={applying || applyingCell != null}
                                  className="rounded p-0.5 text-app-text-subtle opacity-0 group-hover/ek:opacity-100 focus-visible:opacity-100 hover:text-red-600 transition-opacity"
                                  title="EK leeren (auf 0 setzen)"
                                  aria-label="EK leeren"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </span>
                          ) : (
                            ''
                          )}
                        </td>
                        {matrix.suppliers.map((s) => {
                          const offer: PositionQuoteOffer | undefined = p.offers.find((o) => o.quoteId === s.quoteId);
                          const cheapest = offer && offer.quoteId === p.cheapestQuoteId;
                          const applied = offer && offer.quoteId === appliedQuoteId;
                          return (
                            <td key={s.quoteId} className="px-1 py-0.5 text-right">
                              {offer && offer.unitEkPerLvUnit != null ? (
                                <button
                                  type="button"
                                  onClick={() => (applied ? setOpenPositionId(p.id) : void applyCell(p.id, s.quoteId))}
                                  disabled={applying || applyingCell != null || !canEditProjects}
                                  className={`w-full rounded px-1.5 py-1 text-right tabular-nums whitespace-nowrap transition hover:bg-app-hover disabled:cursor-default ${
                                    applied
                                      ? 'bg-emerald-100 text-emerald-800 font-semibold'
                                      : cheapest
                                        ? 'bg-emerald-50 text-emerald-700 font-semibold'
                                        : offer.status === 'proposed'
                                          ? 'border border-dashed border-amber-300 text-amber-800'
                                          : offer.isAlternative
                                            ? 'bg-amber-50 text-app-text'
                                            : 'text-app-text'
                                  }`}
                                  title={`${offer.supplierName}: ${euro4.format(offer.unitEkPerLvUnit)} je ${p.unit ?? 'Einheit'}${
                                    offer.status === 'proposed' ? ' (Vorschlag)' : offer.isAlternative ? ' (Alternativ)' : ''
                                  }${applied ? ' · übernommen, Klick für Details' : canEditProjects ? ' · Klick: als EK übernehmen' : ''}`}
                                >
                                  {applyingCell === `${p.id}:${s.quoteId}` ? (
                                    <span className="inline-flex w-full justify-center"><LoadingSpinner size="xs" /></span>
                                  ) : (
                                    euro4.format(offer.unitEkPerLvUnit)
                                  )}
                                </button>
                              ) : (
                                <span className="text-slate-300">·</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />

      <PositionQuoteOffersPopover
        open={openPositionId != null}
        onClose={() => !applying && setOpenPositionId(null)}
        position={openContext}
        offers={openPosition ?? null}
        applying={applying}
        canApply={canEditProjects}
        onApply={async (offer) => {
          if (!openPositionId) return;
          await applyPrices(
            offer
              ? { mode: 'explicit', picks: [{ position_id: openPositionId, quote_id: offer.quoteId }] }
              : { mode: 'revert', position_ids: [openPositionId] },
          );
          setOpenPositionId(null);
        }}
      />

      <AppConfirmDialog
        open={confirmApply != null}
        onClose={() => setConfirmApply(null)}
        title="Preise übernehmen?"
        description={
          confirmApply ? (
            <>
              <span className="font-medium text-app-text">{confirmApply.label}</span> wird als Material-EK in die Positionen
              des Vorgangs geschrieben; der VK folgt dem Zuschlag. Bestehende EK dieser Positionen werden überschrieben.
              {includeProposed ? ' Vorschläge der KI werden mit übernommen.' : ' Nur bestätigte Zuordnungen, keine Vorschläge oder Alternativen.'}
            </>
          ) : null
        }
        confirmLabel="Übernehmen"
        cancelLabel="Abbrechen"
        isLoading={applying}
        onConfirm={async () => {
          const c = confirmApply;
          setConfirmApply(null);
          if (!c) return;
          await applyPrices(c.mode === 'quote' ? { mode: 'quote', quote_id: c.quoteId } : { mode: 'cheapest' });
        }}
      />
    </section>
    </>
  );
};
